export interface MemberPermissions {
  // 1. Event Operations & Check-In
  checkInMembers?: boolean;
  manualCheckIn?: boolean;
  recordNoShows?: boolean;
  manageEventRosters?: boolean;
  sendEventBroadcasts?: boolean;

  // 2. Game Scores & Leaderboard
  recordMatches?: boolean;
  editMatches?: boolean;
  deleteMatches?: boolean;
  recordBostons?: boolean;
  deleteBostons?: boolean;

  // 3. Event Planning & Scheduling
  createEvents?: boolean;
  editEvents?: boolean;
  deleteEvents?: boolean;

  // 4. Member Directory & Profiles
  viewAdminDirectory?: boolean;
  editMemberProfiles?: boolean;
  managePaidMemberships?: boolean;
  adjustLoyaltyPoints?: boolean;
  awardAchievements?: boolean;
  moderateAccounts?: boolean;

  // 5. Pricing, Packages & Venues
  manageMembershipTiers?: boolean;
  managePointsPackages?: boolean;
  manageVenues?: boolean;
  manageTournaments?: boolean;

  // 6. Club Communications & Content
  postAnnouncements?: boolean;
  moderateFeed?: boolean;
  manageGallery?: boolean;

  // 7. Financials, Audit & System Command
  viewFinancialReports?: boolean;
  viewAuditLogs?: boolean;
  manageSystemTools?: boolean;
}

export type PermissionKey = keyof MemberPermissions;

export interface PermissionDefinition {
  key: PermissionKey;
  label: string;
  description: string;
}

export interface PermissionCategory {
  id: string;
  name: string;
  iconName: string;
  description: string;
  permissions: PermissionDefinition[];
}

export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'event_operations',
    name: 'Event Operations & Check-In',
    iconName: 'ClipboardCheck',
    description: 'On-site event check-in and attendance management',
    permissions: [
      {
        key: 'checkInMembers',
        label: 'Check In Attendees',
        description: 'Scan QR codes and check members into club events directly from mobile screens.'
      },
      {
        key: 'manualCheckIn',
        label: 'Manual Roster Check-In',
        description: 'Search attendee list and mark members checked-in without QR code scanning.'
      },
      {
        key: 'recordNoShows',
        label: 'Mark No-Shows',
        description: 'Mark absent RSVPs as No-Show and record attendance points penalties.'
      },
      {
        key: 'manageEventRosters',
        label: 'Manage RSVPs & Rosters',
        description: 'Add or remove attendees on event rosters and assign table seatings.'
      },
      {
        key: 'sendEventBroadcasts',
        label: 'Send Event Broadcasts',
        description: 'Dispatch push notifications and alerts to registered event attendees.'
      }
    ]
  },
  {
    id: 'matches_leaderboard',
    name: 'Game Scores & Leaderboard',
    iconName: 'Trophy',
    description: 'Match result submission, hand-by-hand logs, and shutout Bostons',
    permissions: [
      {
        key: 'recordMatches',
        label: 'Record Matches',
        description: 'Enter new match results, hand-by-hand trick scores, and scorecard photos.'
      },
      {
        key: 'editMatches',
        label: 'Edit Match Records',
        description: 'Modify previously recorded match scores, player rosters, or hand logs.'
      },
      {
        key: 'deleteMatches',
        label: 'Delete Match Records',
        description: 'Void or remove invalid match records from the official leaderboard.'
      },
      {
        key: 'recordBostons',
        label: 'Record Bostons',
        description: 'Log official shutout Boston bonus games for leaderboard tracking.'
      },
      {
        key: 'deleteBostons',
        label: 'Delete Bostons',
        description: 'Remove or void recorded Boston shutout entries.'
      }
    ]
  },
  {
    id: 'event_planning',
    name: 'Event Planning & Scheduling',
    iconName: 'Calendar',
    description: 'Create, schedule, and publish club sessions and game nights',
    permissions: [
      {
        key: 'createEvents',
        label: 'Create Events',
        description: 'Draft and create new club events and game nights.'
      },
      {
        key: 'editEvents',
        label: 'Edit & Publish Events',
        description: 'Update dates, venues, multipliers, and publish draft events to the club.'
      },
      {
        key: 'deleteEvents',
        label: 'Cancel / Delete Events',
        description: 'Cancel or permanently remove upcoming club events.'
      }
    ]
  },
  {
    id: 'member_management',
    name: 'Member Directory & Profiles',
    iconName: 'Users',
    description: 'Member contact profiles, paid status, loyalty balances, and achievements',
    permissions: [
      {
        key: 'viewAdminDirectory',
        label: 'View Full Member Directory',
        description: 'Access administrative member roster with phone numbers, emails, and street addresses.'
      },
      {
        key: 'editMemberProfiles',
        label: 'Edit Member Profiles',
        description: 'Update member names, nicknames, phone numbers, and addresses.'
      },
      {
        key: 'managePaidMemberships',
        label: 'Assign Paid Status',
        description: 'Assign membership packages, verify dues, or clear paid status.'
      },
      {
        key: 'adjustLoyaltyPoints',
        label: 'Adjust Loyalty Points',
        description: 'Manually award or deduct loyalty reward points with audit explanation notes.'
      },
      {
        key: 'awardAchievements',
        label: 'Grant Achievements',
        description: 'Manually award or revoke trophies and achievement badges for members.'
      },
      {
        key: 'moderateAccounts',
        label: 'Freeze / Ban Accounts',
        description: 'Lock member accounts or apply banned status to filter them out.'
      }
    ]
  },
  {
    id: 'pricing_venues',
    name: 'Pricing, Packages & Venues',
    iconName: 'Coins',
    description: 'Membership packages, points multipliers, venues, and tournaments',
    permissions: [
      {
        key: 'manageMembershipTiers',
        label: 'Membership Tiers & Dues',
        description: 'Configure annual/seasonal membership packages, dues pricing, and valid timeframes.'
      },
      {
        key: 'managePointsPackages',
        label: 'Points Rules & Packages',
        description: 'Configure RSVP, check-in, and Boston bonus point reward values.'
      },
      {
        key: 'manageVenues',
        label: 'Venue Management',
        description: 'Create, edit, and manage club playing venues and locations.'
      },
      {
        key: 'manageTournaments',
        label: 'Tournament Director',
        description: 'Setup and oversee tournament brackets, seedings, and tables.'
      }
    ]
  },
  {
    id: 'communications_content',
    name: 'Club Communications & Content',
    iconName: 'Megaphone',
    description: 'Official announcements, social feed moderation, and photo gallery',
    permissions: [
      {
        key: 'postAnnouncements',
        label: 'Official Announcements',
        description: 'Publish pinned official club announcements and broadcasts to the feed.'
      },
      {
        key: 'moderateFeed',
        label: 'Social Feed Moderation',
        description: 'Pin, hide, or delete community posts and comments.'
      },
      {
        key: 'manageGallery',
        label: 'Photo Gallery Management',
        description: 'Create, edit, and organize club photo albums and media.'
      }
    ]
  },
  {
    id: 'financials_system',
    name: 'Financials, Audit & System Command',
    iconName: 'Shield',
    description: 'Paid revenue reports, audit security trails, and developer tools',
    permissions: [
      {
        key: 'viewFinancialReports',
        label: 'Financial & Paid Reports',
        description: 'View paid membership verification, dues revenue logs, and accounting exports.'
      },
      {
        key: 'viewAuditLogs',
        label: 'Audit Logs',
        description: 'View timestamped audit logs of all administrative actions.'
      },
      {
        key: 'manageSystemTools',
        label: 'System Tools & Diagnostics',
        description: 'Run database maintenance, system diagnostics, and clear server caches.'
      }
    ]
  }
];

// All permission keys as a typed array
export const ALL_PERMISSION_KEYS: PermissionKey[] = PERMISSION_CATEGORIES.flatMap(c => 
  c.permissions.map(p => p.key)
);

// Presets for quick assignment
export const PERMISSION_PRESETS: Record<string, { label: string; description: string; permissions: Partial<Record<PermissionKey, boolean>> }> = {
  checkinHelper: {
    label: 'Check-in Helper',
    description: 'On-site check-in and attendance recording (Scan QR, manual check-in, no-shows)',
    permissions: {
      checkInMembers: true,
      manualCheckIn: true,
      recordNoShows: true,
      manageEventRosters: true
    }
  },
  scorekeeper: {
    label: 'Scorekeeper / Judge',
    description: 'Match entry, scorecard logging, and Boston shutout recording',
    permissions: {
      recordMatches: true,
      editMatches: true,
      recordBostons: true
    }
  },
  eventCoordinator: {
    label: 'Event Coordinator',
    description: 'Create, schedule, and edit events, manage rosters, and send broadcasts',
    permissions: {
      checkInMembers: true,
      manualCheckIn: true,
      manageEventRosters: true,
      sendEventBroadcasts: true,
      createEvents: true,
      editEvents: true,
      recordMatches: true,
      recordBostons: true
    }
  },
  membershipAdmin: {
    label: 'Membership Officer',
    description: 'Member directory, profile updates, dues verification, and point adjustments',
    permissions: {
      viewAdminDirectory: true,
      editMemberProfiles: true,
      managePaidMemberships: true,
      adjustLoyaltyPoints: true,
      awardAchievements: true,
      viewFinancialReports: true
    }
  },
  communityModerator: {
    label: 'Community Moderator',
    description: 'Feed announcements, post moderation, and photo gallery management',
    permissions: {
      postAnnouncements: true,
      moderateFeed: true,
      manageGallery: true
    }
  }
};

/**
 * Determine if a user profile is a master Super User
 */
export function isSuperUser(profile: any): boolean {
  if (!profile) return false;
  return profile.isSuper === true || profile.adminRole === 'super';
}

/**
 * Check if a profile has a specific permission.
 * Super Users have all permissions implicitly.
 * Also checks legacy fallback roles (organizer, tech_helper, event_host) for backwards compatibility.
 */
export function hasPermission(profile: any, key: PermissionKey): boolean {
  if (!profile) return false;
  if (isSuperUser(profile)) return true;

  // Check explicit granular permission
  if (profile.permissions && profile.permissions[key] === true) {
    return true;
  }

  // Backwards compatibility for legacy roles during transition
  const role = profile.adminRole || '';
  if (role === 'organizer') {
    // Organizers previously had all admin permissions except tech system tools
    if (key === 'manageSystemTools' || key === 'viewAuditLogs') return false;
    return true;
  }
  if (role === 'tech_helper') {
    if (['manageSystemTools', 'viewAuditLogs', 'viewAdminDirectory', 'editMemberProfiles', 'manageGallery'].includes(key)) {
      return true;
    }
  }
  if (role === 'event_host') {
    if (['checkInMembers', 'manualCheckIn', 'recordNoShows', 'manageEventRosters', 'sendEventBroadcasts', 'recordMatches', 'editMatches', 'recordBostons'].includes(key)) {
      return true;
    }
  }

  return false;
}

/**
 * Permissions that require accessing the Admin command center tab
 * (Notice: checkIn alone does NOT require the admin tab!)
 */
const ADMIN_TAB_PERMISSIONS: PermissionKey[] = [
  'recordMatches',
  'editMatches',
  'deleteMatches',
  'recordBostons',
  'deleteBostons',
  'createEvents',
  'editEvents',
  'deleteEvents',
  'viewAdminDirectory',
  'editMemberProfiles',
  'managePaidMemberships',
  'adjustLoyaltyPoints',
  'awardAchievements',
  'moderateAccounts',
  'manageMembershipTiers',
  'managePointsPackages',
  'manageVenues',
  'manageTournaments',
  'viewFinancialReports',
  'viewAuditLogs',
  'manageSystemTools'
];

/**
 * Check if user should see the Admin tab in navigation and access /admin
 */
export function canAccessAdmin(profile: any): boolean {
  if (!profile) return false;
  if (isSuperUser(profile)) return true;

  return ADMIN_TAB_PERMISSIONS.some(key => hasPermission(profile, key));
}

/**
 * Check if user can check attendees into events (can be done directly on feed/events without admin tab)
 */
export function canCheckIn(profile: any): boolean {
  if (!profile) return false;
  return isSuperUser(profile) || hasPermission(profile, 'checkInMembers') || hasPermission(profile, 'manualCheckIn');
}

/**
 * Check if user can enter or manage match records
 */
export function canManageRecords(profile: any): boolean {
  if (!profile) return false;
  return isSuperUser(profile) || hasPermission(profile, 'recordMatches') || hasPermission(profile, 'editMatches') || hasPermission(profile, 'recordBostons');
}
